import { Link2 } from 'lucide-react';
import React, { useRef, useState } from 'react';

import type { ComparisonData } from '../../../../types/blog';

interface Props {
  data: ComparisonData;
  onChange: (data: ComparisonData) => void;
}

const textareaCls = 'ds-textarea pr-8';

interface LinkPopoverProps { onInsert: (url: string, anchor: string) => void; onClose: () => void; }
const LinkPopover: React.FC<LinkPopoverProps> = ({ onInsert, onClose }) => {
  const [url, setUrl] = useState('https://');
  const [anchor, setAnchor] = useState('');
  return (
    <div className="ds-card-pop absolute right-0 top-full mt-1 z-30 w-64 flex flex-col gap-2">
      <p className="t-card-title">Insert link</p>
      <input autoFocus value={url} onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter') { e.preventDefault(); if (url.trim() && url !== 'https://') onInsert(url.trim(), anchor.trim()); } }}
        placeholder="https://example.com"
        className="ds-input ds-input-sm" />
      <input value={anchor} onChange={(e) => setAnchor(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter') { e.preventDefault(); if (url.trim() && url !== 'https://') onInsert(url.trim(), anchor.trim()); } }}
        placeholder="Anchor text (optional)"
        className="ds-input ds-input-sm" />
      <div className="flex gap-2">
        <button type="button" onClick={() => { if (url.trim() && url !== 'https://') onInsert(url.trim(), anchor.trim()); }}
          disabled={!url.trim() || url === 'https://'}
          className="ds-btn ds-btn-primary ds-btn-sm flex-1 disabled:opacity-40">Insert</button>
        <button type="button" onClick={onClose}
          className="ds-btn ds-btn-secondary ds-btn-sm flex-1">Cancel</button>
      </div>
    </div>
  );
};

const LinkedTextarea: React.FC<{ label: string; labelCls: string; borderCls: string; value: string; onChange: (v: string) => void }> = ({
  label, labelCls, borderCls, value, onChange,
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [showLink, setShowLink] = useState(false);

  const handleInsert = (url: string, anchor: string) => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: ss, selectionEnd: se } = el;
    const text = anchor || value.slice(ss, se) || 'link';
    const md = `[${text}](${url})`;
    onChange(value.slice(0, ss) + md + value.slice(se));
    setShowLink(false);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(ss, ss + md.length); });
  };

  return (
    <div className="flex flex-col gap-1">
      <label className={`t-label ${labelCls}`}>{label}</label>
      <div className="relative">
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          placeholder={`Describe the '${label.toLowerCase()}' state…`}
          className={`${textareaCls} ${borderCls}`}
        />
        <button
          type="button"
          title="Insert link"
          onClick={() => setShowLink((v) => !v)}
          className="ds-btn ds-btn-ghost ds-btn-sm absolute right-1.5 top-1.5 w-6 h-6 p-0"
          style={showLink ? { color: 'var(--ds-brand)' } : undefined}
        >
          <Link2 className="w-3.5 h-3.5" />
        </button>
        {showLink && <LinkPopover onInsert={handleInsert} onClose={() => setShowLink(false)} />}
      </div>
    </div>
  );
};

export const ComparisonSectionEditor: React.FC<Props> = ({ data, onChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <LinkedTextarea
      label="Before"
      labelCls="t-danger"
      borderCls=""
      value={data.before}
      onChange={(v) => onChange({ ...data, before: v })}
    />
    <LinkedTextarea
      label="After"
      labelCls="t-ok"
      borderCls=""
      value={data.after}
      onChange={(v) => onChange({ ...data, after: v })}
    />
  </div>
);
